/*
 * quantity-limits.js
 * Bootstrapped per-product order limits for the Shopify Debut theme.
 * No app, no third-party data. A client-side COURTESY guardrail: it keeps
 * ordinary shoppers from accidentally over-ordering and steers bulk buyers
 * to contact you instead. It is not, and cannot be, hard enforcement.
 *
 * INSTALL
 *   1. Online Store > Themes > Debut > Actions > Edit code
 *   2. Assets > Add a new asset > upload this file (quantity-limits.js)
 *   3. Edit the PRODUCT_LIMITS object below
 *   4. Open layout/theme.liquid, just before </body> add:
 *        {{ 'quantity-limits.js' | asset_url | script_tag }}
 *   5. Save. Test a limited product page and the cart page.
 *
 * HOW TO GET A PRODUCT ID (stable; survives renames)
 *   In admin click into the product; the URL ends in the ID:
 *     admin.shopify.com/store/you/products/1234567890  ->  1234567890
 *
 * WHAT THE LIMIT MEANS
 *   Max TOTAL quantity of that product per order, combined across variants.
 *
 * ASSUMPTION
 *   Add-to-cart is a form submit (Debut default). If your theme adds purely
 *   via a click handler that never submits the form, the product-page guard
 *   won't fire and only the cart-page check applies.
 */
(function () {
  'use strict';

  // ===== 1. CONFIGURE PRODUCT LIMITS =======================================
  // Key   = numeric product ID.
  // Value = { max: <positive integer>, bulkUrl: '<url>' }
  //         A bare number also works for a product with no bulk link.
  var PRODUCT_LIMITS = {
    8048872718574: { max: 2, bulkUrl: 'https://store.churchinitiative.org/s.nl/it.A/id.2651/.f?sc=2&category=14' },
    5465684967585: { max: 2, bulkUrl: 'https://store.churchinitiative.org/s.nl/it.A/id.2510/.f?sc=2&category=11' },
    9397980266734: { max: 2, bulkUrl: 'https://store.churchinitiative.org/s.nl/it.A/id.2789/.f?sc=2&category=9957' }
  };

  function limitMessage(maxQty) {
    return 'Limit ' + maxQty + ' per order for this item.';
  }
  // =========================================================================

  // Sanitize config: keep only positive-integer limits (accepting a bare
  // number or a { max, bulkUrl } object), keyed by string ID.
  var VALID_LIMITS = {}; // productId -> max quantity
  var BULK_URLS = {};    // productId -> optional bulk-order URL
  Object.keys(PRODUCT_LIMITS).forEach(function (productId) {
    var entry = PRODUCT_LIMITS[productId];
    var isObject = entry !== null && typeof entry === 'object';
    var max = Number(isObject ? entry.max : entry);
    var bulkUrl = isObject ? entry.bulkUrl : null;
    if (Number.isFinite(max) && max > 0 && Math.floor(max) === max) {
      VALID_LIMITS[String(productId)] = max;
      if (typeof bulkUrl === 'string' && bulkUrl) BULK_URLS[String(productId)] = bulkUrl;
    } else {
      console.warn('[quantity-limits] ignoring invalid limit for', productId, '=', entry);
    }
  });

  // ---- helpers ------------------------------------------------------------
  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (response) {
      if (!response.ok) throw new Error('cart ' + response.status);
      return response.json();
    });
  }

  function handleFromUrl() {
    var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function resolveProductId() {
    try {
      var analytics = window.ShopifyAnalytics;
      if (analytics && analytics.meta && analytics.meta.product && analytics.meta.product.id) {
        return Promise.resolve(String(analytics.meta.product.id));
      }
    } catch (e) {}
    var handle = handleFromUrl();
    if (!handle) return Promise.resolve(null);
    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (product) { return product ? String(product.id) : null; })
      .catch(function () { return null; });
  }

  // Total quantity of a given product currently in the cart, across variants.
  function qtyInCart(cart, productId) {
    var targetId = String(productId);
    return cart.items.reduce(function (sum, item) {
      return String(item.product_id) === targetId ? sum + item.quantity : sum;
    }, 0);
  }

  // Transient bottom-of-screen message (product-page limit warnings).
  function notify(text) {
    var toast = document.getElementById('qlimit-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'qlimit-toast';
      toast.setAttribute('role', 'status');
      toast.style.cssText =
        'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);' +
        'background:#111;color:#fff;padding:12px 18px;border-radius:6px;' +
        'font:14px/1.4 sans-serif;z-index:99999;max-width:90%;text-align:center;' +
        'box-shadow:0 4px 14px rgba(0,0,0,.25);opacity:0;transition:opacity .2s;';
      document.body.appendChild(toast);
    }
    toast.textContent = text;
    requestAnimationFrame(function () { toast.style.opacity = '1'; });
    clearTimeout(toast._hideTimer);
    toast._hideTimer = setTimeout(function () { toast.style.opacity = '0'; }, 3500);
  }

  // ---- product page -------------------------------------------------------
  // The main product form(s). Excludes recommended/related product sections
  // so their (different) products aren't wrongly limited.
  function mainProductForms() {
    var forms = Array.prototype.slice.call(
      document.querySelectorAll('form[action*="/cart/add"]')
    );
    return forms.filter(function (form) {
      return !form.closest(
        '#shopify-section-product-recommendations, .product-recommendations,' +
        '[data-section-type*="recommend"], .related-products, .related'
      );
    });
  }

  function initProductPage() {
    var forms = mainProductForms();
    if (!forms.length) return;

    resolveProductId().then(function (productId) {
      if (productId == null || !(productId in VALID_LIMITS)) return;
      var maxQty = VALID_LIMITS[productId];
      var bulkUrl = BULK_URLS[productId];

      // Hide "Buy it now" only within the limited product's form(s),
      // since dynamic checkout skips the cart entirely.
      forms.forEach(function (form) {
        form.querySelectorAll('.shopify-payment-button').forEach(function (button) {
          button.style.display = 'none';
        });
      });

      // countInCart: how many of this product we believe are in the cart.
      // cartKnown: whether we've successfully read the real cart at least once.
      var cartState = { countInCart: 0, cartKnown: false };
      var reconcileTimerEarly, reconcileTimerFinal;

      function remainingQty() { return Math.max(0, maxQty - cartState.countInCart); }

      // Persistent note under the quantity selector so the limit is known up
      // front. Content is static (limit number + optional bulk link), so it's
      // built once per form; the number comes from maxQty so it can't drift.
      function ensureNote(form) {
        if (form.querySelector('.qlimit-note')) return;

        var note = document.createElement('div');
        note.className = 'qlimit-note';
        note.setAttribute('role', 'note');
        // Neutral, self-contained styling; restyle here to match the theme.
        note.style.cssText = 'margin:6px 0 0;font:15px/1.5 sans-serif;color:#555;';

        var limitLine = document.createElement('div');
        limitLine.textContent = 'Limit ' + maxQty + ' per order.';
        note.appendChild(limitLine);

        if (bulkUrl) {
          var bulkLine = document.createElement('div');
          bulkLine.appendChild(document.createTextNode('or for '));
          var link = document.createElement('a');
          link.href = bulkUrl;
          link.target = '_blank';
          link.rel = 'noopener noreferrer'; // safe off-site link
          link.style.textDecoration = 'underline';
          link.textContent = 'Group Orders';
          bulkLine.appendChild(link);
          note.appendChild(bulkLine);
        }

        var quantityInput = form.querySelector('input[name="quantity"]');
        var anchor = quantityInput &&
          (quantityInput.closest('.product-form__item, .js-qty, .product-form__quantity') || quantityInput);
        if (anchor && anchor.parentNode) {
          anchor.parentNode.insertBefore(note, anchor.nextSibling);
        } else {
          form.appendChild(note);
        }
      }

      // Inline alert shown at the add-to-cart area, styled like the cart-page
      // banner so limit messages appear where the shopper is looking (not as
      // an easily-missed floating toast). One per form, reused, auto-hides.
      function productAlert(form, text) {
        var alert = form.querySelector('.qlimit-alert');
        if (!alert) {
          alert = document.createElement('div');
          alert.className = 'qlimit-alert';
          alert.setAttribute('role', 'alert');
          alert.style.cssText =
            'background:#fdecea;color:#611a15;border:1px solid #f5c6c3;' +
            'padding:12px 16px;border-radius:6px;margin:0 0 12px;font:14px/1.4 sans-serif;';
          var addButton = form.querySelector('[name="add"], [type="submit"]');
          // Insert above the button group as a block so flex layouts aren't broken.
          var anchor = addButton &&
            (addButton.closest('.product-form__buttons, .product-form__controls-group, .product-form__item') || addButton);
          if (anchor && anchor.parentNode) {
            anchor.parentNode.insertBefore(alert, anchor);
          } else {
            form.appendChild(alert);
          }
        }
        alert.textContent = text;
        alert.style.display = '';
        clearTimeout(alert._hideTimer);
        alert._hideTimer = setTimeout(function () { alert.style.display = 'none'; }, 6000);
      }

      // Reflect the current allowance in every form: cap the quantity field,
      // disable Add when nothing remains, and (re)render the limit note.
      function applyRemaining() {
        forms.forEach(function (form) {
          var quantityInput = form.querySelector('input[name="quantity"]');
          if (quantityInput) quantityInput.max = Math.max(1, remainingQty());
          // Keep Add to Cart clickable even at the limit, so a click always
          // produces an explanatory message. A silently disabled button just
          // confuses shoppers; enforcement happens in the submit guard below.
          var addButton = form.querySelector('[name="add"], [type="submit"]');
          if (addButton) addButton.disabled = false;
          ensureNote(form);
        });
      }

      function setAddDisabled(disabled) {
        forms.forEach(function (form) {
          var addButton = form.querySelector('[name="add"], [type="submit"]');
          if (addButton) addButton.disabled = disabled;
        });
      }

      // allowDecrease=false (early reconcile) may raise the count toward the
      // real cart but never below the optimistic total, so a slow add request
      // that hasn't landed yet can't erase the protection. allowDecrease=true
      // is authoritative (load, tab return, and the final reconcile pass).
      function refresh(allowDecrease) {
        return getCart()
          .then(function (cart) {
            var actualCount = qtyInCart(cart, productId);
            cartState.countInCart = allowDecrease
              ? actualCount
              : Math.max(cartState.countInCart, actualCount);
            cartState.cartKnown = true;
            applyRemaining();
            return true;
          })
          .catch(function () {
            // Preserve the last known state. An initial failure leaves
            // cartKnown=false (fail open); a later failure keeps the prior count.
            return false;
          });
      }

      // Two-stage reconcile: the early pass tightens only; the later pass is
      // authoritative in case the add failed or the quantity was adjusted.
      function scheduleReconcile() {
        clearTimeout(reconcileTimerEarly);
        clearTimeout(reconcileTimerFinal);
        reconcileTimerEarly = setTimeout(function () { refresh(false); }, 900);
        reconcileTimerFinal = setTimeout(function () { refresh(true); }, 2000);
      }

      forms.forEach(function (form) {
        var quantityInput = form.querySelector('input[name="quantity"]');
        ensureNote(form); // render the static "Limit N" line right away
        if (quantityInput) {
          quantityInput.addEventListener('change', function () {
            var enteredQty = parseInt(quantityInput.value, 10);
            if (cartState.cartKnown && enteredQty > remainingQty()) {
              quantityInput.value = Math.max(1, remainingQty());
              productAlert(form, limitMessage(maxQty));
            }
          });
        }

        // Guard on SUBMIT ONLY. A form submit already covers button clicks
        // and Enter-key adds; guarding click as well would process the same
        // add twice and wrongly block valid multi-quantity additions.
        form.addEventListener('submit', function (event) {
          var submitInput = form.querySelector('input[name="quantity"]');
          var requestedQty = submitInput ? Math.max(1, parseInt(submitInput.value, 10) || 1) : 1;
          // If the cart is unknown, still block an egregious single add.
          var allowedQty = cartState.cartKnown ? remainingQty() : maxQty;
          if (requestedQty > allowedQty) {
            event.preventDefault();
            event.stopImmediatePropagation();
            productAlert(form,
              cartState.cartKnown && cartState.countInCart >= maxQty
                ? 'You already have the maximum (' + maxQty + ') in your cart.'
                : limitMessage(maxQty)
            );
            return;
          }
          // Allowed: count it optimistically so a fast repeat is caught,
          // then reconcile against the real cart shortly after.
          if (cartState.cartKnown) {
            cartState.countInCart += requestedQty;
            applyRemaining();
          }
          scheduleReconcile();
        }, true);
      });

      // Hold Add to Cart until the first cart lookup resolves, so a fast
      // double-add during the load window can't slip past. applyRemaining()
      // re-enables it afterward (or on failure, failing open).
      setAddDisabled(true);
      refresh(true).then(function () { applyRemaining(); });
      window.addEventListener('pageshow', function () { refresh(true); });
      document.addEventListener('visibilitychange', function () {
        if (!document.hidden) refresh(true);
      });
    });
  }

  // ---- cart page ----------------------------------------------------------
  function initCartPage() {
    var onCartPage = /^\/cart/.test(window.location.pathname) ||
      document.body.classList.contains('template-cart');
    if (!onCartPage) return;

    var cartForm = document.querySelector('form[action="/cart"], form.cart');
    var checkoutBlocked = false;

    function standardCheckoutButtons() {
      return document.querySelectorAll(
        '[name="checkout"], .cart__submit, .cart__checkout'
      );
    }
    function expressCheckoutElements() {
      return document.querySelectorAll(
        '.additional-checkout-buttons, .dynamic-checkout__content'
      );
    }
    function setStandardCheckoutDisabled(disabled) {
      standardCheckoutButtons().forEach(function (button) {
        button.disabled = disabled;
        button.style.opacity = disabled ? '0.5' : '';
        button.style.cursor = disabled ? 'not-allowed' : '';
      });
    }
    function setExpressCheckoutHidden(hidden) {
      expressCheckoutElements().forEach(function (el) { el.style.display = hidden ? 'none' : ''; });
    }

    function showBanner(exceededIds, cart) {
      var banner = document.getElementById('qlimit-banner');
      if (!banner) {
        banner = document.createElement('div');
        banner.id = 'qlimit-banner';
        banner.setAttribute('role', 'alert');
        banner.style.cssText =
          'background:#fdecea;color:#611a15;border:1px solid #f5c6c3;' +
          'padding:12px 16px;border-radius:6px;margin:0 0 16px;font-size:14px;';
        var host = cartForm || document.querySelector('main') || document.body;
        host.insertBefore(banner, host.firstChild);
      }
      banner.textContent = '';
      exceededIds.forEach(function (productId) {
        var matchedItem = cart.items.find(function (item) {
          return String(item.product_id) === productId;
        }) || {};
        var line = document.createElement('div');
        var nameEl = document.createElement('strong');
        nameEl.textContent = matchedItem.product_title || ('Product ' + productId);
        line.appendChild(document.createTextNode('Please reduce '));
        line.appendChild(nameEl);
        line.appendChild(document.createTextNode(' to ' + VALID_LIMITS[productId] + ' or fewer to check out.'));
        banner.appendChild(line);
      });
    }
    function removeBanner() {
      var banner = document.getElementById('qlimit-banner');
      if (banner) banner.remove();
    }

    function unblockCheckout() {
      checkoutBlocked = false;
      setStandardCheckoutDisabled(false);
      setExpressCheckoutHidden(false);
      removeBanner();
    }

    // allowUnblock=false lets a pass tighten (block) but never release, so an
    // early check during a slow cart update can't reopen checkout on stale data.
    function validate(allowUnblock) {
      return getCart()
        .then(function (cart) {
          var totalsByProduct = {};
          cart.items.forEach(function (item) {
            var productId = String(item.product_id);
            if (productId in VALID_LIMITS) {
              totalsByProduct[productId] = (totalsByProduct[productId] || 0) + item.quantity;
            }
          });
          var exceededIds = Object.keys(totalsByProduct).filter(function (productId) {
            return totalsByProduct[productId] > VALID_LIMITS[productId];
          });
          if (exceededIds.length) {
            checkoutBlocked = true;
            setStandardCheckoutDisabled(true);
            setExpressCheckoutHidden(true);
            showBanner(exceededIds, cart);
          } else if (allowUnblock) {
            unblockCheckout();
          }
        })
        .catch(function () {
          // Fail open only on an authoritative pass: don't sabotage a real
          // checkout if we can't read the cart during a transient failure.
          if (allowUnblock) unblockCheckout();
        });
    }

    // Hold BOTH standard and express checkout until first validation resolves,
    // so an already-over-limit cart can't be checked out during the load window.
    setStandardCheckoutDisabled(true);
    setExpressCheckoutHidden(true);
    checkoutBlocked = true;

    // Hard stop on submit based on explicit state, not on the banner element.
    (cartForm || document).addEventListener('submit', function (event) {
      if (checkoutBlocked) {
        event.preventDefault();
        event.stopImmediatePropagation();
        notify('Please reduce the flagged items to continue.');
      }
    }, true);

    validate(true);

    // On a quantity change: block first (fail-safe during the update), then
    // an early tighten-only pass and a later authoritative pass. This survives
    // a slow cart update without reopening checkout against stale data.
    var revalidateEarly, revalidateFinal;
    document.addEventListener('change', function (event) {
      if (event.target && event.target.matches('input[type="number"], input[name^="updates"]')) {
        checkoutBlocked = true;
        setStandardCheckoutDisabled(true);
        setExpressCheckoutHidden(true);
        clearTimeout(revalidateEarly);
        clearTimeout(revalidateFinal);
        revalidateEarly = setTimeout(function () { validate(false); }, 600);
        revalidateFinal = setTimeout(function () { validate(true); }, 1600);
      }
    });
  }

  // ---- init ---------------------------------------------------------------
  function init() {
    if (!Object.keys(VALID_LIMITS).length) return;
    initProductPage();
    initCartPage();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();